/**
 * frontend/src/pages/valuation/_DisclaimerAccept.tsx
 *
 * VAL-PAY-FLOW-1 (was VAL-TERMS-ACCEPT-1) — the estimate disclaimer an owner
 * accepts BEFORE the pay screen: step 4 -> disclaimer -> pay -> Your value.
 * Conspicuous notice + affirmative act: the box starts UNTICKED and the button
 * stays disabled until it is ticked (a bare "continue" is not assent — Berman
 * v. Freedom Financial, 9th Cir. 2022). Every word shown comes from the server,
 * so what is recorded (its hash) is exactly what was on screen. No Terms of
 * Service link here: the platform ToS is TosGate's.
 */
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  acceptValuationDisclaimer, fetchValuationDisclaimer, type ValuationDisclaimer,
} from '@/api/valuationDisclaimer'
import { formatApiError } from '@/utils/httpErrors'

export function DisclaimerAccept({ engagementId, onAccepted }: { engagementId: string; onAccepted: () => void }) {
  const [disclaimer, setDisclaimer] = useState<ValuationDisclaimer | null>(null)
  const [ticked, setTicked] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchValuationDisclaimer(engagementId)
      .then(d => { if (live) { setDisclaimer(d); setTicked(false) } })
      .catch((err: unknown) => { if (live) setError(formatApiError(err)) })
    return () => { live = false }
  }, [engagementId])

  const accept = async () => {
    if (!disclaimer || !ticked || saving) return
    setSaving(true)
    setError(null)
    try {
      await acceptValuationDisclaimer(engagementId, disclaimer)
      onAccepted()
    } catch (err: unknown) {
      setError(formatApiError(err))
      // The text may have changed under us: show the current version, unticked.
      fetchValuationDisclaimer(engagementId).then(d => { setDisclaimer(d); setTicked(false) }).catch(() => {})
    } finally {
      setSaving(false)
    }
  }

  if (!disclaimer && !error) {
    // VAL-FIX-PAY-BUGS-1 bug 1: never an empty box (title and body blank) while the text loads.
    return (
      <Card data-testid="disclaimer-loading">
        <CardContent className="space-y-3 p-6">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card data-testid="disclaimer-accept">
      <CardHeader>
        <CardTitle>{disclaimer?.title ?? ''}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-pal-ink-body">
        {disclaimer && (
          <>
            <p>{disclaimer.notice}</p>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={ticked}
                onChange={e => setTicked(e.target.checked)}
              />
              <span>{disclaimer.checkbox_label}</span>
            </label>
            <Button disabled={!ticked || saving} onClick={() => { void accept() }}>
              {disclaimer.button_label}
            </Button>
          </>
        )}
        {error && <p className="text-red-700">{error}</p>}
      </CardContent>
    </Card>
  )
}
